import React, { useState, useEffect } from 'react';
import { VTTN_CIRCULATING, VTTN_HOLDERS, VTTN_PRICE_USD, VTTN_TOTAL_SUPPLY } from '../constants/vttn';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Coins,
  TrendingUp,
  TrendingDown,
  Users,
  Copy,
  ExternalLink,
  ArrowLeft,
  Activity,
  BarChart3,
  DollarSign,
  Star,
  RefreshCw,
  Eye,
  Download,
  Hash,
  Clock,
  Zap,
  ArrowUpRight,
  ArrowDownLeft
} from 'lucide-react';

interface TokenDetail {
  address: string;
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  circulatingSupply: string;
  price: number;
  priceChange24h: number;
  marketCap: number;
  volume24h: number;
  holders: number;
  transfers24h: number;
  type: 'native' | 'erc20' | 'energy' | 'staking';
  verified: boolean;
  logo?: string;
  description?: string;
  website?: string;
  twitter?: string;
  telegram?: string;
  discord?: string;
  github?: string;
  whitepaper?: string;
  maxSupply?: string;
  burnedTokens?: string;
  createdAt?: string;
  creator?: string;
}

interface TokenHolder {
  address: string;
  balance: string;
  percentage: number;
  rank: number;
  label?: string;
}

interface TokenTransaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  timestamp: string;
  status: 'success' | 'failed';
  type: 'transfer' | 'mint' | 'burn' | 'approve';
  gasUsed: number;
  gasPrice: number;
}

const TokenDetailPage: React.FC = () => {
  const { address } = useParams<{ address: string }>();
  const navigate = useNavigate();
  const [token, setToken] = useState<TokenDetail | null>(null);
  const [holders, setHolders] = useState<TokenHolder[]>([]);
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'holders' | 'transactions'>('overview');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    if (address) {
      loadTokenDetail(address);
    }
  }, [address]);

  const loadTokenDetail = async (tokenAddress: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const VTTN_TOKEN_ADDRESS = '0x0Eb3Dade9b6366E28DF7D789894e297b165725F5';
      
      // 실제 Voltron 토큰 정보 조회
      try {
        const [addressResponse, statsResponse, txResponse, holdersResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/explorer/addresses/${tokenAddress}`),
          fetch(`${apiBaseUrl}/explorer/network-stats`),
          fetch(`${apiBaseUrl}/explorer/addresses/${tokenAddress}/transactions?limit=50`),
          fetch(`${apiBaseUrl}/explorer/tokens/${tokenAddress}/holders?limit=100`)
        ]);
        
        if (addressResponse.ok && statsResponse.ok) {
          const addressData = await addressResponse.json();
          const statsData = await statsResponse.json();
          const txData = txResponse.ok ? await txResponse.json() : { transactions: [] };
          const holdersData = holdersResponse.ok ? await holdersResponse.json() : { holders: [], total: 0 };
          
          const circulatingSupply = VTTN_CIRCULATING;
          const price = statsData.stats?.vttnPrice || 0.5;
          
          const tokenDetail: TokenDetail = {
            address: tokenAddress,
            name: 'VOLTRON Token',
            symbol: 'VTTN',
            decimals: 18,
            totalSupply: '1000000000', // 10억
            circulatingSupply: circulatingSupply.toString(),
            price: price,
            priceChange24h: statsData.stats?.priceChange || 0,
            marketCap: price * circulatingSupply, // 유통량 기준 마켓캡
            volume24h: 0,
            holders: holdersData.total || 0,
            transfers24h: txData.transactions?.filter((tx: any) => parseFloat(tx.value || '0') > 0).length || 0,
            type: 'native',
            verified: true,
            description: 'VOLTRON Token (VTTN)은 Voltron Mainnet의 네이티브 토큰으로, 네트워크의 거래 수수료, 스테이킹, 거버넌스 등에 사용됩니다.',
            website: 'https://voltron.app',
            twitter: 'https://twitter.com/voltronnetwork',
            github: 'https://github.com/voltronnetwork',
            maxSupply: '1000000000',
            burnedTokens: '0',
            createdAt: '2024-01-01T00:00:00Z',
            creator: '0x0000000000000000000000000000000000000000'
          };
          
          // 홀더 정보 매핑
          const holders: TokenHolder[] = (holdersData.holders || []).map((holder: any) => ({
            address: holder.address,
            balance: holder.balance,
            percentage: parseFloat(holder.percentage || '0'),
            rank: holder.rank,
            label: undefined
          }));
          
          // 트랜잭션 매핑 (value가 0인 것 제외)
          const transactions: TokenTransaction[] = (txData.transactions || [])
            .filter((tx: any) => parseFloat(tx.value || '0') > 0) // value가 0보다 큰 것만
            .map((tx: any) => ({
              hash: tx.hash,
              from: tx.from,
              to: tx.to,
              value: tx.value || '0',
              timestamp: typeof tx.timestamp === 'number' 
                ? new Date(tx.timestamp * 1000).toISOString() 
                : new Date(tx.timestamp).toISOString(),
              status: tx.status || 'success',
              type: 'transfer',
              gasUsed: parseInt(tx.gasUsed || '0'),
              gasPrice: parseFloat(tx.gasPrice || '0') / 1e9
            }));
          
          setToken(tokenDetail);
          setHolders(holders);
          setTransactions(transactions);
          setLoading(false);
          return;
        }
      } catch (apiError) {
        console.error('API 호출 실패:', apiError);
      }
      
      // API 실패 시 기본 Voltron 토큰 정보
      const circulatingSupply = VTTN_CIRCULATING;
      const defaultToken: TokenDetail = {
        address: tokenAddress,
        name: 'VOLTRON Token',
        symbol: 'VTTN',
        decimals: 18,
        totalSupply: '1000000000',
        circulatingSupply: circulatingSupply.toString(),
        price: 0.5,
        priceChange24h: 0,
        marketCap: 0.5 * circulatingSupply,
        volume24h: 0,
        holders: VTTN_HOLDERS,
        transfers24h: 0,
        type: 'native',
        verified: true,
        description: 'VOLTRON Token (VTTN)은 Voltron Mainnet의 네이티브 토큰입니다.',
        maxSupply: '1000000000',
        burnedTokens: '0',
        createdAt: '2024-01-01T00:00:00Z',
        creator: '0x0000000000000000000000000000000000000000'
      };
      
      setToken(defaultToken);
      setHolders([]);
      setTransactions([]);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load token detail:', error);
      setError('토큰 정보를 불러오는데 실패했습니다.');
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}초 전`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}분 전`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}시간 전`;
    return `${Math.floor(seconds / 86400)}일 전`;
  };

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'transfer': return 'bg-blue-100 text-blue-800';
      case 'mint': return 'bg-green-100 text-green-800';
      case 'burn': return 'bg-red-100 text-red-800';
      case 'approve': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const paginatedHolders = holders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const paginatedTransactions = transactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(
    (activeTab === 'holders' ? holders.length : transactions.length) / itemsPerPage
  );

  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="space-y-6"
      >
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      </motion.div>
    );
  }

  if (error || !token) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="space-y-6"
      >
        <div className="text-center py-20">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">토큰을 찾을 수 없습니다</h1>
          <p className="text-gray-600 mb-6">{error || '요청하신 토큰이 존재하지 않거나 잘못된 주소입니다.'}</p>
          <button
            onClick={() => navigate('/tokens')}
            className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            토큰 목록으로 돌아가기
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="space-y-6"
    >
      {/* 헤더 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/tokens')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="h-16 w-16 rounded-full bg-gradient-to-r from-orange-400 to-red-500 flex items-center justify-center">
              <Coins className="w-8 h-8 text-white" />
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl font-bold text-gray-900">{token.name}</h1>
                {token.verified && (
                  <span className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full flex items-center space-x-1">
                    <Star className="w-3 h-3" />
                    <span>Verified</span>
                  </span>
                )}
                <span className="bg-gray-100 text-gray-800 text-sm px-3 py-1 rounded-full">
                  {token.type}
                </span>
              </div>
              <p className="text-gray-500 mt-1">{token.symbol}</p>
              <p className="text-sm text-gray-600 mt-2 max-w-2xl">{token.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => loadTokenDetail(address!)}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* 주소 정보 */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">컨트랙트 주소</p>
              <div className="flex items-center space-x-2">
                <span className="font-mono text-gray-900">{token.address}</span>
                <button
                  onClick={() => copyToClipboard(token.address)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => window.open(`/address/${token.address}`, '_blank')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
            {token.website && (
              <div className="flex items-center space-x-2">
                <a
                  href={token.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  웹사이트
                </a>
                {token.twitter && (
                  <a
                    href={token.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Twitter
                  </a>
                )}
                {token.github && (
                  <a
                    href={token.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    GitHub
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">가격</p>
              <p className="text-2xl font-bold text-gray-900">${token.price.toFixed(4)}</p>
              <div className={`flex items-center space-x-1 mt-1 ${
                token.priceChange24h >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {token.priceChange24h >= 0 ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">
                  {token.priceChange24h >= 0 ? '+' : ''}{token.priceChange24h.toFixed(2)}%
                </span>
              </div>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">시가총액</p>
              <p className="text-2xl font-bold text-gray-900">${(token.marketCap / 1000000).toFixed(2)}M</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">24시간 거래량</p>
              <p className="text-2xl font-bold text-gray-900">${(token.volume24h / 1000000).toFixed(2)}M</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Activity className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">총 홀더</p>
              <p className="text-2xl font-bold text-gray-900">{token.holders.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Users className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => {
                setActiveTab('overview');
                setCurrentPage(1);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              개요
            </button>
            <button
              onClick={() => {
                setActiveTab('holders');
                setCurrentPage(1);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'holders'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              홀더 ({token.holders.toLocaleString()})
            </button>
            <button
              onClick={() => {
                setActiveTab('transactions');
                setCurrentPage(1);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'transactions'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              트랜잭션 ({token.transfers24h.toLocaleString()})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 토큰 정보 */}
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">토큰 정보</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">총 공급량:</span>
                      <span className="font-medium">{parseFloat(token.totalSupply).toLocaleString()} {token.symbol}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">유통 공급량:</span>
                      <span className="font-medium">{parseFloat(token.circulatingSupply).toLocaleString()} {token.symbol}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">유통률:</span>
                      <span className="font-medium">
                        {((parseFloat(token.circulatingSupply) / parseFloat(token.totalSupply)) * 100).toFixed(2)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">소수점 자리:</span>
                      <span className="font-medium">{token.decimals}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">24시간 전송:</span>
                      <span className="font-medium">{token.transfers24h.toLocaleString()}</span>
                    </div>
                    {token.maxSupply && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">최대 공급량:</span>
                        <span className="font-medium">{parseFloat(token.maxSupply).toLocaleString()} {token.symbol}</span>
                      </div>
                    )}
                    {token.createdAt && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">생성일:</span>
                        <span className="font-medium">{new Date(token.createdAt).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* 상위 홀더 미리보기 */}
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">상위 홀더</h3>
                    <button
                      onClick={() => setActiveTab('holders')}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      전체 보기
                    </button>
                  </div>
                  <div className="space-y-3">
                    {holders.slice(0, 5).map((holder) => (
                      <div key={holder.address} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-medium text-gray-500">#{holder.rank}</span>
                          <button
                            onClick={() => window.open(`/address/${holder.address}`, '_blank')}
                            className="font-mono text-sm text-blue-600 hover:text-blue-800"
                          >
                            {formatAddress(holder.address)}
                          </button>
                          {holder.label && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {holder.label}
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{parseFloat(holder.balance).toFixed(2)} {token.symbol}</p>
                          <p className="text-xs text-gray-500">{holder.percentage.toFixed(2)}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'holders' && (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>순위</th>
                      <th>주소</th>
                      <th>잔액</th>
                      <th>비율</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedHolders.map((holder) => (
                      <tr key={holder.address} className="hover:bg-gray-50">
                        <td>
                          <span className="font-medium text-gray-900">#{holder.rank}</span>
                        </td>
                        <td>
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Users className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <button
                                onClick={() => window.open(`/address/${holder.address}`, '_blank')}
                                className="font-medium text-blue-600 hover:text-blue-800"
                              >
                                {formatAddress(holder.address)}
                              </button>
                              {holder.label && (
                                <p className="text-sm text-gray-500 mt-1">{holder.label}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="font-medium text-gray-900">
                            {parseFloat(holder.balance).toFixed(2)} {token.symbol}
                          </span>
                        </td>
                        <td>
                          <span className="text-sm font-medium text-gray-900">
                            {holder.percentage.toFixed(2)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {activeTab === 'transactions' && (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>트랜잭션 해시</th>
                      <th>타입</th>
                      <th>From</th>
                      <th>To</th>
                      <th>수량</th>
                      <th>상태</th>
                      <th>시간</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTransactions.map((tx) => (
                      <tr key={tx.hash} className="hover:bg-gray-50">
                        <td>
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Hash className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <button
                                onClick={() => window.open(`/transactions/${tx.hash}`, '_blank')}
                                className="font-medium text-blue-600 hover:text-blue-800"
                              >
                                {tx.hash.slice(0, 10)}...{tx.hash.slice(-8)}
                              </button>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-sm text-gray-500 font-mono">
                                  {tx.hash.substring(0, 10)}...{tx.hash.slice(-8)}
                                </span>
                                <button
                                  onClick={() => copyToClipboard(tx.hash)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`inline-flex items-center px-2 py-1 rounded-lg text-sm font-medium ${
                            getTypeColor(tx.type)
                          }`}>
                            {tx.type}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => window.open(`/address/${tx.from}`, '_blank')}
                            className="font-mono text-sm text-blue-600 hover:text-blue-800"
                          >
                            {formatAddress(tx.from)}
                          </button>
                        </td>
                        <td>
                          <button
                            onClick={() => window.open(`/address/${tx.to}`, '_blank')}
                            className="font-mono text-sm text-blue-600 hover:text-blue-800"
                          >
                            {formatAddress(tx.to)}
                          </button>
                        </td>
                        <td>
                          <span className="text-sm font-medium text-green-600">
                            {parseFloat(tx.value).toFixed(2)} {token.symbol}
                          </span>
                        </td>
                        <td>
                          <span className={`inline-flex items-center px-2 py-1 rounded-lg text-sm font-medium ${
                            tx.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {tx.status}
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {formatTimeAgo(tx.timestamp)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* 페이지네이션 */}
          {(activeTab === 'holders' || activeTab === 'transactions') && totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-6">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                이전
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                if (page > totalPages) return null;
                
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 border rounded-lg ${
                      currentPage === page
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                다음
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default TokenDetailPage;